// =============================================================================
// app/settings/account.tsx — email / username / display name.
//
// P1-PROFILE redesign: the old layout was three tall single-field cards with
// heavy padding = acres of dead space. Rebuilt as ONE grouped glass card with
// hairline-divided rows (label + inline input + caption), a "Profile" group for
// the display name, and inline username validation. Full-screen ParticleBackground
// replaces the flat fill. All API/business logic (PATCH /users/me) is unchanged.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  ScrollView,
  Alert,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '../../components/ui/Icon';
import { api } from '../../services/api';
import { GlassCard, GlassButton } from '../../components/glass';
import ParticleBackground from '../../components/ui/ParticleBackground';
import { colors, spacing, radius, fonts, typographyScale, getLayout } from '../../constants/theme';

// Username IS editable (PATCH /users/me accepts it, validated + unique on the
// backend). There is no editable phone field on the backend, so it isn't here.
interface AccountData {
  email: string;
  username: string;
  display_name: string;
}

const USERNAME_RE = /^[a-zA-Z0-9_-]{3,32}$/;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function AccountScreen() {
  const { width } = useWindowDimensions();
  const layout = getLayout(width);
  const containerStyle = layout !== 'phone' ? { maxWidth: layout === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const } : undefined;
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [focused, setFocused] = useState<keyof AccountData | null>(null);
  const [data, setData] = useState<AccountData>({
    email: '',
    username: '',
    display_name: '',
  });
  const [original, setOriginal] = useState<AccountData>({
    email: '',
    username: '',
    display_name: '',
  });

  // Any editable field drives the Save button.
  const hasChanges =
    data.email !== original.email ||
    data.username !== original.username ||
    data.display_name !== original.display_name;

  // Inline validation — surfaced under the field, not as a post-save alert.
  const usernameError = useMemo(() => {
    if (data.username === original.username) return null;
    if (!data.username.trim()) return null;
    return USERNAME_RE.test(data.username.trim()) ? null : 'Use 3–32 letters, numbers, _ or -';
  }, [data.username, original.username]);

  const emailError = useMemo(() => {
    if (data.email === original.email) return null;
    if (!data.email.trim()) return null;
    return EMAIL_RE.test(data.email.trim()) ? null : 'Enter a valid email address';
  }, [data.email, original.email]);

  const fetchAccount = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    // /auth/me returns { data: { user: {...} } } with camelCase fields; api()
    // unwraps one level, so the user is at res.data.user.
    const res = await api<{ user: any }>('/auth/me');
    const u = res.success ? res.data?.user : null;
    if (u) {
      const d: AccountData = {
        email: u.email || '',
        username: u.username || '',
        display_name: u.displayName || '',
      };
      setData(d);
      setOriginal(d);
    } else if (!res.success) {
      setLoadError(res.error || 'Failed to load account info.');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchAccount();
  }, [fetchAccount]);

  async function handleSave() {
    // Validate a changed username locally before hitting the API (mirrors the
    // backend rules: 3–32 chars, letters/numbers/_/- only). The backend also
    // enforces uniqueness and returns a clear error if it's taken.
    const usernameChanged = data.username !== original.username;
    if (usernameChanged && !USERNAME_RE.test(data.username.trim())) {
      Alert.alert(
        'Invalid username',
        'Username must be 3–32 characters and use only letters, numbers, underscores, or hyphens.'
      );
      return;
    }

    setSaving(true);
    // PATCH /users/me whitelists camelCase keys. Send only changed fields so we
    // don't trip uniqueness checks on values that didn't change.
    const payload: Record<string, any> = {};
    if (data.email !== original.email) payload.email = data.email;
    if (data.display_name !== original.display_name) payload.displayName = data.display_name;
    if (usernameChanged) payload.username = data.username.trim().toLowerCase();

    const res = await api('/users/me', {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
    setSaving(false);

    if (res.success) {
      // The backend lowercases the username — reflect what was actually saved.
      const saved = { ...data, username: data.username.trim().toLowerCase() };
      setData(saved);
      setOriginal(saved);
      Alert.alert('Saved', 'Your account info has been updated.');
    } else {
      Alert.alert('Error', res.error || 'Failed to save changes.');
    }
  }

  const canSave = hasChanges && !saving && !usernameError && !emailError;

  if (loading) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </View>
    );
  }

  if (loadError) {
    return (
      <View style={styles.screen}>
        <ParticleBackground theme="light" />
        <View style={styles.loadingWrap}>
          <Ionicons name="alert-circle-outline" size={48} color={colors.faint} />
          <Text style={styles.errorText}>{loadError}</Text>
          <GlassButton title="Retry" onPress={fetchAccount} variant="secondary" size="sm" />
        </View>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <ParticleBackground theme="light" />
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={[styles.scrollContent, containerStyle]}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Profile group */}
          <Text style={styles.sectionTitle}>Profile</Text>
          <GlassCard padding={0}>
            <FieldRow
              label="Display Name"
              value={data.display_name}
              onChangeText={(v) => setData({ ...data, display_name: v })}
              icon="person-outline"
              placeholder="Your display name"
              autoCapitalize="words"
              focused={focused === 'display_name'}
              onFocus={() => setFocused('display_name')}
              onBlur={() => setFocused(null)}
            />
            <FieldRow
              label="Username"
              value={data.username}
              onChangeText={(v) => setData({ ...data, username: v.replace(/[^a-zA-Z0-9_-]/g, '') })}
              icon="at-outline"
              caption="3–32 characters · letters, numbers, _ and -"
              error={usernameError}
              placeholder="username"
              focused={focused === 'username'}
              onFocus={() => setFocused('username')}
              onBlur={() => setFocused(null)}
              isLast
            />
          </GlassCard>

          {/* Account group */}
          <Text style={styles.sectionTitle}>Account</Text>
          <GlassCard padding={0}>
            <FieldRow
              label="Email"
              value={data.email}
              onChangeText={(v) => setData({ ...data, email: v })}
              keyboardType="email-address"
              icon="mail-outline"
              error={emailError}
              placeholder="you@example.com"
              focused={focused === 'email'}
              onFocus={() => setFocused('email')}
              onBlur={() => setFocused(null)}
              isLast
            />
          </GlassCard>

          <GlassButton
            title="Save Changes"
            onPress={handleSave}
            loading={saving}
            disabled={!canSave}
            fullWidth
            style={styles.saveBtn}
          />
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

function FieldRow({
  label,
  value,
  onChangeText,
  keyboardType = 'default',
  icon,
  caption,
  error,
  placeholder,
  autoCapitalize = 'none',
  focused,
  onFocus,
  onBlur,
  isLast,
}: {
  label: string;
  value: string;
  onChangeText: (v: string) => void;
  keyboardType?: 'default' | 'email-address' | 'phone-pad';
  icon?: string;
  caption?: string;
  error?: string | null;
  placeholder?: string;
  autoCapitalize?: 'none' | 'words' | 'sentences';
  focused?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  isLast?: boolean;
}) {
  return (
    <View style={[styles.fieldRow, !isLast && styles.fieldRowBorder]}>
      <View style={styles.fieldHeader}>
        {icon && <Ionicons name={icon as any} size={15} color={colors.muted} />}
        <Text style={styles.fieldLabel}>{label}</Text>
      </View>
      <TextInput
        style={[
          styles.fieldInput,
          focused && styles.fieldInputFocused,
          !!error && styles.fieldInputError,
        ]}
        value={value}
        onChangeText={onChangeText}
        keyboardType={keyboardType}
        autoCapitalize={autoCapitalize}
        autoCorrect={false}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholderTextColor={colors.faint}
        placeholder={placeholder}
      />
      {error ? (
        <Text style={styles.fieldError}>{error}</Text>
      ) : caption ? (
        <Text style={styles.fieldCaption}>{caption}</Text>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.bg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.lg,
  },
  errorText: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
  },
  scrollContent: {
    padding: spacing.lg,
  },
  sectionTitle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
    marginTop: spacing.lg,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    ...fonts.bold,
  },
  fieldRow: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
  },
  fieldRowBorder: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.cardBorder,
  },
  fieldHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  fieldLabel: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.semibold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  fieldInput: {
    ...typographyScale.body,
    fontSize: 16,
    color: colors.ink,
    backgroundColor: colors.bg,
    borderRadius: radius.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderWidth: 1,
    borderColor: colors.cardBorder,
    ...fonts.regular,
  },
  fieldInputFocused: {
    borderColor: colors.primary,
    backgroundColor: colors.white,
  },
  fieldInputError: {
    borderColor: colors.error,
  },
  fieldCaption: {
    ...typographyScale.caption,
    fontSize: 11,
    color: colors.muted,
    marginTop: spacing.xs,
    ...fonts.regular,
  },
  fieldError: {
    ...typographyScale.caption,
    fontSize: 11,
    color: colors.error,
    marginTop: spacing.xs,
    ...fonts.medium,
  },
  saveBtn: {
    marginTop: spacing.xl,
  },
});
